/**
 * Добровольная работа хозяйства (core S6): чистая модель экрана — подписи,
 * группы каталога, поля хозяйства, тело запроса. Без React и без сети, чтобы
 * правила («полив не записывается», «площадь не названа ≠ 0», «интервал
 * упорядочен») проверялись тестом, а не глазами.
 *
 * Добровольная работа в процент выполнения не входит и хозяйства между собой
 * не сравнивает: здесь нет ни счёта «сделано/план», ни сортировки по числу
 * записей.
 */

import type { Schemas } from '../../../api/client';
import { defineMessages } from '../../../i18n/define';
import type { Message } from '../../../i18n/define';
import { runtimeLocale, tr } from '../../../i18n/runtime';

const m = defineMessages({
  catTillage: { id: 'voluntary.model.category.tillage', defaultMessage: 'Вспашка', description: 'Категория вида работы в списке выбора добровольной работы и в таблице: вспашка' },
  catSowing: { id: 'voluntary.model.category.sowing', defaultMessage: 'Посев', description: 'Категория вида работы в списке выбора добровольной работы и в таблице: посев' },
  catIrrigation: { id: 'voluntary.model.category.irrigation', defaultMessage: 'Полив', description: 'Категория вида работы: полив' },
  catTreatment: { id: 'voluntary.model.category.treatment', defaultMessage: 'Обработка', description: 'Категория вида работы в списке выбора добровольной работы и в таблице: обработка растений' },
  catHarvest: { id: 'voluntary.model.category.harvest', defaultMessage: 'Сбор', description: 'Категория вида работы в списке выбора добровольной работы и в таблице: сбор урожая' },
  catInspection: { id: 'voluntary.model.category.inspection', defaultMessage: 'Карантинное обследование', description: 'Категория вида работы: карантинное обследование' },
  catQuarantine: { id: 'voluntary.model.category.quarantine', defaultMessage: 'Карантинные меры', description: 'Категория вида работы: карантинные меры' },
  catOther: { id: 'voluntary.model.category.other', defaultMessage: 'Прочее', description: 'Категория вида работы в списке выбора добровольной работы и в таблице: прочее' },
  fieldLabel: { id: 'voluntary.model.field', defaultMessage: 'Контур № {no}', description: 'Название поля хозяйства в списке выбора при записи добровольной работы; {no} — номер контура' },
  fieldLabelPart: { id: 'voluntary.model.fieldPart', defaultMessage: 'Контур № {no} · часть {part}', description: 'Название части поля хозяйства в списке выбора; {no} — номер контура, {part} — обозначение части' },
  areaError: { id: 'voluntary.model.areaError', defaultMessage: 'Площадь — число больше нуля или пустое поле «не названа».', description: 'Ошибка поля площади в форме добровольной работы; «не названа» — подпись пустой площади' },
  pickWork: { id: 'voluntary.model.pickWork', defaultMessage: 'Выберите, какая работа сделана.', description: 'Подсказка рядом с кнопкой формы добровольной работы: не выбран вид работы' },
  pickField: { id: 'voluntary.model.pickField', defaultMessage: 'Отметьте хотя бы одно поле.', description: 'Подсказка рядом с кнопкой формы добровольной работы: не отмечено ни одного поля' },
  pickPeriod: { id: 'voluntary.model.pickPeriod', defaultMessage: 'Укажите начало и конец периода.', description: 'Подсказка рядом с кнопкой формы добровольной работы: период не заполнен' },
  periodBackwards: { id: 'voluntary.model.periodBackwards', defaultMessage: 'Начало периода позже конца.', description: 'Подсказка рядом с кнопкой формы добровольной работы: начало периода позже конца' },
  pickDate: { id: 'voluntary.model.pickDate', defaultMessage: 'Укажите дату работы.', description: 'Подсказка рядом с кнопкой формы добровольной работы: не указана дата' },
  fieldProblem: { id: 'voluntary.model.fieldProblem', defaultMessage: '{field}: {problem}', description: 'Подсказка рядом с кнопкой формы: проблема в конкретном поле; {field} — название поля, {problem} — что не так' },
  whenRange: { id: 'voluntary.model.whenRange', defaultMessage: '{from} — {to}', description: 'Период дат добровольной работы в таблице: начало и конец' },
  authorFarmer: { id: 'voluntary.model.author.farmer', defaultMessage: 'фермер', description: 'Колонка «Внёс» в таблице добровольных работ: запись внёс сам фермер хозяйства' },
  authorManager: { id: 'voluntary.model.author.manager', defaultMessage: 'менеджер за хозяйство', description: 'Колонка «Внёс» в таблице добровольных работ: запись внёс менеджер от имени хозяйства' },
});

export type VoluntaryWork = Schemas['VoluntaryWorkOut'];
export type VoluntaryList = Schemas['VoluntaryWorkList'];
export type VoluntaryBody = Schemas['VoluntaryWorkIn'];
type CatalogItem = Schemas['CatalogWorkOut'];
type LandUse = Schemas['ActorLandUseOut'];

export const MICROHA_PER_HA = 1_000_000;

/** Категории вида работы словами; неизвестная — «прочее», а не сырой код. */
const CATEGORY_MESSAGE: Record<string, Message> = {
  tillage: m.catTillage,
  sowing: m.catSowing,
  irrigation: m.catIrrigation,
  treatment: m.catTreatment,
  harvest: m.catHarvest,
  q_inspection: m.catInspection,
  quarantine_measure: m.catQuarantine,
  other: m.catOther,
};

const sortLocale = () => (runtimeLocale() === 'ru' ? 'ru' : runtimeLocale());

/**
 * Категории, которые добровольной работой не записываются: полив (закон №725 —
 * только файл ирригации) и обследование карантина (работа инспектора).
 */
const NOT_VOLUNTARY = new Set(['irrigation', 'q_inspection']);

export function categoryName(code: string): string {
  return tr(CATEGORY_MESSAGE[code] ?? m.catOther);
}

export interface CatalogGroup {
  category: string;
  label: string;
  items: CatalogItem[];
}

/** Каталог для выбора: без полива, по категориям, внутри — по имени. */
export function catalogGroups(items: readonly CatalogItem[]): CatalogGroup[] {
  const byCategory = new Map<string, CatalogItem[]>();
  for (const item of items) {
    if (NOT_VOLUNTARY.has(item.category)) continue;
    const list = byCategory.get(item.category) ?? [];
    list.push(item);
    byCategory.set(item.category, list);
  }
  return [...byCategory.entries()]
    .map(([category, rows]) => ({
      category,
      label: categoryName(category),
      items: [...rows].sort((a, b) => a.name.localeCompare(b.name, sortLocale())),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, sortLocale()));
}

export interface FieldOption {
  /** Ключ выбора: контур и часть. */
  key: string;
  contourId: number;
  partId: number | null;
  label: string;
  /** Площадь по реестру, га; `null` — не названа. */
  areaHa: number | null;
  /** Культуры опорного сезона по порядку; пусто — культура сезона не проставлена. */
  crops: string[];
}

/**
 * Поля хозяйства, которыми оно пользуется на дату: земля, а не чужая и не
 * прошлая. Первыми — поля с культурой сезона (`cropsOf`): добровольную работу
 * записывают в основном по ним; внутри групп — по номеру контура.
 */
export function fieldOptions(landUses: readonly LandUse[], onDate: string, cropsOf: (contourId: number) => string[] = () => []): FieldOption[] {
  const seen = new Set<string>();
  const out: FieldOption[] = [];
  for (const use of landUses) {
    if (use.role !== 'user') continue;
    if (use.valid_from && use.valid_from > onDate) continue;
    if (use.valid_to && use.valid_to <= onDate) continue;
    const key = `${use.contour_id}:${use.part_id ?? 0}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const micro = use.arable_microha ?? use.total_microha;
    out.push({
      key,
      contourId: use.contour_id,
      partId: use.part_id,
      label: use.part_label
        ? tr(m.fieldLabelPart, { no: String(use.contour_no), part: String(use.part_label) })
        : tr(m.fieldLabel, { no: String(use.contour_no) }),
      // Нулевая площадь реестра — «не названа»: предлагать её как значение нельзя.
      areaHa: micro === null || micro === undefined || micro <= 0 ? null : micro / MICROHA_PER_HA,
      crops: cropsOf(use.contour_id),
    });
  }
  return out.sort(
    (a, b) =>
      Number(b.crops.length > 0) - Number(a.crops.length > 0) ||
      a.label.localeCompare(b.label, sortLocale(), { numeric: true }),
  );
}

/** Площадь из поля ввода (га, «1,5» или «1.5») в микрогектары; пусто — `null`, не ноль. */
export function parseAreaHa(text: string): { micro: number | null; error: string | null } {
  const raw = text.trim().replace(',', '.');
  if (raw === '') return { micro: null, error: null };
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) {
    return { micro: null, error: tr(m.areaError) };
  }
  return { micro: Math.round(value * MICROHA_PER_HA), error: null };
}

export interface FormState {
  catalogWorkId: number | null;
  /** Выбранные поля: ключ → введённая площадь, га (строка поля ввода). */
  picked: Record<string, string>;
  interval: boolean;
  on: string;
  from: string;
  to: string;
  note: string;
}

export interface Draft {
  body: VoluntaryBody | null;
  /** Что мешает отправить, словами; пусто — можно. */
  problem: string | null;
}

/** Тело запроса из формы или причина, по которой его нет. */
export function buildBody(
  form: FormState,
  options: readonly FieldOption[],
  actorId: number | null,
): Draft {
  if (form.catalogWorkId === null) return { body: null, problem: tr(m.pickWork) };
  const chosen = options.filter((option) => option.key in form.picked);
  if (chosen.length === 0) return { body: null, problem: tr(m.pickField) };
  if (form.interval) {
    if (!form.from || !form.to) return { body: null, problem: tr(m.pickPeriod) };
    if (form.from > form.to) return { body: null, problem: tr(m.periodBackwards) };
  } else if (!form.on) {
    return { body: null, problem: tr(m.pickDate) };
  }
  const fields: VoluntaryBody['fields'] = [];
  for (const option of chosen) {
    const area = parseAreaHa(form.picked[option.key] ?? '');
    if (area.error) return { body: null, problem: tr(m.fieldProblem, { field: option.label, problem: area.error }) };
    fields.push({
      contour_id: option.contourId,
      contour_part_id: option.partId,
      area_microha: area.micro,
    });
  }
  return {
    problem: null,
    body: {
      actor_id: actorId,
      catalog_work_id: form.catalogWorkId,
      fields,
      date_precision: form.interval ? 'interval' : 'day',
      occurred_on: form.interval ? null : form.on,
      occurred_from: form.interval ? form.from : null,
      occurred_to: form.interval ? form.to : null,
      note: form.note.trim(),
    },
  };
}

/** Дата записи словами: один день или период. */
export function whenLabel(
  row: Pick<VoluntaryWork, 'occurred_on' | 'occurred_from' | 'occurred_to'>,
  fmt: (iso: string) => string,
): string {
  if (row.occurred_on) return fmt(row.occurred_on);
  if (row.occurred_from && row.occurred_to) return tr(m.whenRange, { from: fmt(row.occurred_from), to: fmt(row.occurred_to) });
  return '—';
}

/** Кто внёс: хозяйство само или диспетчер за него. */
export function authorLabel(row: Pick<VoluntaryWork, 'entered_on_behalf'>): string {
  return tr(row.entered_on_behalf ? m.authorManager : m.authorFarmer);
}
